import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { ManagerRole } from '../../api/managerApi';
import { blockBackdropClose, FormDialogTitle, LoadingState } from '../common';
import DataTable, { type DataTableColumn } from '../common/DataTable';
import { useEventContext } from '../../contexts/EventContext';
import { useEventCollectFlagsQuery } from '../../hooks/useEventCollectFlagsQuery';
import { useGraphCategoriesQuery } from '../../hooks/useGraphCategoriesQuery';
import { useResultsQuery } from '../../hooks/useResultsQuery';
import { EVENT_LIST_ROUTE_PATH } from '../../routes/appRoutes';
import { eventService } from '../../services/eventService';
import type { GraphCategory } from '../../services/graphApi';
import { loadMyManagerProfile } from '../../services/managerService';
import {
  downloadResults,
  fetchResultDownloadFile,
  updateResult,
  type FinishStatus,
  type ResultDownloadFormat,
  type ResultSplit,
  type ResultTableRow,
  type SplitDistributionSplit,
} from '../../services/resultApi';

type ResultFilters = {
  gender: string;
  categoryId: string;
  ageGroup: string;
  group: string;
  bibKeyword: string;
};

type ResultRecordDisplayMode = 'chiptime' | 'measuredTime';

type FilterOption = {
  label: string;
  value: string;
};

const emptyFilters: ResultFilters = {
  gender: '',
  categoryId: '',
  ageGroup: '',
  group: '',
  bibKeyword: '',
};

const RESULT_PAGE_SIZE = 100;
const TIME_INPUT_PATTERN = /^\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?$/;
const downloadFormats: ReadonlyArray<{ format: ResultDownloadFormat; label: string }> = [
  { format: 'pdf', label: 'PDF' },
  { format: 'xlsx', label: 'XLSX' },
  { format: 'csv', label: 'CSV' },
];
const recordDisplayModeOptions: ReadonlyArray<{ mode: ResultRecordDisplayMode; label: string }> = [
  { mode: 'chiptime', label: '측정 기록' },
  { mode: 'measuredTime', label: '측정 시각' },
];
const downloadMimeTypes: Record<ResultDownloadFormat, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv;charset=utf-8',
};

function getStartSplit(row: ResultTableRow) {
  return row.splits[0];
}

function getFinishSplit(row: ResultTableRow) {
  return row.splits.at(-1);
}

function getFinishStatus(row: ResultTableRow): FinishStatus | undefined {
  return row.finishStatus;
}

function getFinishStatusLabel(status: FinishStatus | undefined) {
  switch (status) {
    case 'FINISHED':
      return '완주';
    case 'DNF':
      return '중도포기';
    case 'DNS':
      return '출발안함';
    case 'NEEDS_REVIEW':
      return '검토필요';
    default:
      return '-';
  }
}

// 운영자가 확정할 수 있는 완주 상태 (NEEDS_REVIEW는 확정 대상이 아니다)
type ConfirmableFinishStatus = Exclude<FinishStatus, 'NEEDS_REVIEW'>;

function isNotFinisher(row: ResultTableRow) {
  const status = getFinishStatus(row);
  return status === 'DNF' || status === 'DNS';
}

function getSplitById(row: ResultTableRow, splitId: number) {
  return row.splits.find((split) => split.splitId === splitId);
}

function isModifiedResultRow(row: ResultTableRow) {
  return row.splits.some((split) => split.modified);
}

function getModifyReason(row: ResultTableRow) {
  return row.splits.find((split) => split.modified)?.modifyReason;
}

function rowNeedsReview(row: ResultTableRow) {
  return getFinishStatus(row) === 'NEEDS_REVIEW';
}

function getResultRowSx(row: ResultTableRow) {
  if (isNotFinisher(row)) {
    return {
      '& > td': {
        backgroundColor: '#e2e8f0',
      },
      '&:hover > td': {
        backgroundColor: '#cbd5e1',
      },
    };
  }

  if (rowNeedsReview(row)) {
    return {
      '& > td': {
        backgroundColor: '#fee2e2',
      },
      '&:hover > td': {
        backgroundColor: '#fecaca',
      },
    };
  }

  return isModifiedResultRow(row)
    ? {
        '& > td': {
          backgroundColor: '#ecfdf3',
        },
        '&:hover > td': {
          backgroundColor: '#dcfce7',
        },
      }
    : undefined;
}

function renderResultTimeCell(
  split: ResultSplit | undefined,
  value: string,
  isMissingPreviousRecordCell: boolean
) {
  if (isMissingPreviousRecordCell) {
    return (
      <span className="results-missing-record" title="뒤 구간 기록은 있지만 이 구간 기록이 없습니다.">
        누락
      </span>
    );
  }

  return split?.modified ? <span className="results-modified-cell">{value || '-'}</span> : value;
}

function renderModifyReasonCell(row: ResultTableRow) {
  if (!isModifiedResultRow(row)) {
    return '';
  }

  return (
    <span className="results-modified-help" title={getModifyReason(row) || '수정 사유가 없습니다.'}>
      ?
    </span>
  );
}

function getRecordDisplayTime(split: ResultSplit | undefined, mode: ResultRecordDisplayMode) {
  if (!split) {
    return '';
  }

  return mode === 'measuredTime'
    ? split.timingPointTime ?? ''
    : split.chiptimeElapsedTime ?? '';
}

function createResultColumns(
  timingPointSplits: SplitDistributionSplit[],
  recordDisplayMode: ResultRecordDisplayMode
): DataTableColumn<ResultTableRow>[] {
  return [
    {
      key: 'rowNumber',
      header: '순위',
      width: 64,
      render: (row: ResultTableRow) => (isNotFinisher(row) ? getFinishStatus(row) : row.rowNumber),
    },
    { key: 'bibNumber', header: '배번호', width: 96 },
    { key: 'chipNumber', header: '칩번호', width: 96 },
    { key: 'name', header: '이름', width: 120 },
    { key: 'gender', header: '성별', width: 80 },
    { key: 'category', header: '종목', width: 130 },
    { key: 'ageGroup', header: '나이그룹', width: 100 },
    { key: 'group', header: '그룹', width: 100 },
    {
      key: 'startRecordTime',
      header: '시작지점 기록 시간',
      width: 150,
      render: (row: ResultTableRow) => {
        const split = getStartSplit(row);
        const value = getRecordDisplayTime(split, recordDisplayMode);

        return renderResultTimeCell(split, value, rowNeedsReview(row) && !value);
      },
    },
    ...timingPointSplits.map((split) => ({
      key: `timingPoint-${split.splitId}`,
      header: split.splitName,
      width: 150,
      render: (row: ResultTableRow) => {
        const rowSplit = getSplitById(row, split.splitId);
        const value = getRecordDisplayTime(rowSplit, recordDisplayMode);

        return renderResultTimeCell(rowSplit, value, rowNeedsReview(row) && !value);
      },
    })),
    {
      key: 'finishRecordTime',
      header: '끝지점 기록 시간',
      width: 150,
      render: (row: ResultTableRow) => {
        const split = getFinishSplit(row);
        const value = getRecordDisplayTime(split, recordDisplayMode);

        return renderResultTimeCell(split, value, rowNeedsReview(row) && !value);
      },
    },
    { key: 'totalElapsedTime', header: '총 소요시간', width: 130 },
    {
      key: 'modified',
      header: '수정여부',
      width: 56,
      align: 'center',
      render: renderModifyReasonCell,
    },
  ];
}

function toFilterOptions(values: string[]): FilterOption[] {
  return values.map((value) => ({ label: value, value }));
}

function sanitizeFileName(value: string) {
  return value.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').trim();
}

function getYearText(dateText: string) {
  return dateText.split('-')[0] || new Date().getFullYear().toString();
}

function createDefaultDownloadFileBaseName(
  eventName: string | null,
  eventDate: string
) {
  const year = getYearText(eventDate);
  const name = sanitizeFileName(eventName || '대회');

  return `${year}_${name}_결과`;
}

function withFileExtension(fileName: string, format: ResultDownloadFormat) {
  return `${fileName.replace(/\.(pdf|xlsx|csv)$/i, '')}.${format}`.normalize('NFC');
}

function sanitizeDownloadBaseName(value: string) {
  return sanitizeFileName(value).replace(/\.(pdf|xlsx|csv)$/i, '');
}

async function toDownloadBlob(blob: Blob, format: ResultDownloadFormat) {
  if (format === 'csv') {
    return new Blob(['\ufeff', await blob.text()], { type: downloadMimeTypes.csv });
  }

  return new Blob([blob], { type: downloadMimeTypes[format] });
}

function triggerDownload(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
    globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function filterRowsInCurrentPage(rows: ResultTableRow[], filters: ResultFilters) {
  return rows.filter((row) => {
    if (filters.gender && row.gender !== filters.gender) {
      return false;
    }
    if (filters.ageGroup && row.ageGroup !== filters.ageGroup) {
      return false;
    }
    if (filters.group && row.group !== filters.group) {
      return false;
    }

    return true;
  });
}

function getSplitRecordLabel(split: ResultTableRow['splits'][number], index: number, totalCount: number) {
  if (index === 0) {
    return '시작점 측정 시각';
  }

  if (index === totalCount - 1) {
    return '종료지점 측정 시각';
  }

  return `${split.splitName} 측정 시각`;
}

function FilterSelect({
  label,
  allLabel,
  value,
  options,
  disabled,
  onChange,
}: Readonly<{
  label: string;
  allLabel: string;
  value: string;
  options: FilterOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
}>) {
  return (
    <select
      aria-label={label}
      className="results-filter-control"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option value={option.value} key={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function ResultsDashboard() {
  const queryClient = useQueryClient();
  const { selectedEventId, selectedEventName } = useEventContext();
  const collectFlagsQuery = useEventCollectFlagsQuery(selectedEventId);
  const collectBirth = collectFlagsQuery.data?.collectBirth ?? true;
  const collectGender = collectFlagsQuery.data?.collectGender ?? true;
  const [draftFilters, setDraftFilters] = useState<ResultFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<ResultFilters>(emptyFilters);
  const [page, setPage] = useState(1);
  const [eventDate, setEventDate] = useState('');
  const [selectedRow, setSelectedRow] = useState<ResultTableRow | null>(null);
  const [recordDrafts, setRecordDrafts] = useState<Record<string, string>>({});
  const [modifyReasonDraft, setModifyReasonDraft] = useState('');
  const [finishStatusDraft, setFinishStatusDraft] = useState<ConfirmableFinishStatus | null>(null);
  const [modalError, setModalError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('');
  const [downloadCategoryId, setDownloadCategoryId] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [downloadingFormat, setDownloadingFormat] = useState<ResultDownloadFormat | null>(null);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const [recordDisplayMode, setRecordDisplayMode] = useState<ResultRecordDisplayMode>('chiptime');
  const [showNeedsReviewOnly, setShowNeedsReviewOnly] = useState(false);
  const categoryQuery = useGraphCategoriesQuery(selectedEventId);
  const categories = useMemo(() => categoryQuery.data ?? [], [categoryQuery.data]);
  const draftSelectedCategory = useMemo(
    () => categories.find((category) => String(category.id) === draftFilters.categoryId) ?? null,
    [categories, draftFilters.categoryId]
  );
  // 백엔드 응답에 남아있는 값과 무관하게, 미수집 항목은 필터를 무조건 전체로 고정한다.
  const resultQueryParams = useMemo(() => ({
    page: page - 1,
    size: RESULT_PAGE_SIZE,
    categoryId: appliedFilters.categoryId ? Number(appliedFilters.categoryId) : undefined,
    keyword: appliedFilters.bibKeyword,
    gender: collectGender && appliedFilters.gender ? appliedFilters.gender : undefined,
    ageGroup: collectBirth && appliedFilters.ageGroup ? appliedFilters.ageGroup : undefined,
    group: appliedFilters.group || undefined,
    finishStatus: showNeedsReviewOnly ? ('NEEDS_REVIEW' as const) : undefined,
  }), [
    appliedFilters.ageGroup,
    appliedFilters.bibKeyword,
    appliedFilters.categoryId,
    appliedFilters.gender,
    appliedFilters.group,
    collectBirth,
    collectGender,
    page,
    showNeedsReviewOnly,
  ]);
  const { data, error, isError, isFetching, isLoading, refetch } =
    useResultsQuery(selectedEventId, resultQueryParams);
  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const totalCount = data?.totalCount ?? 0;
  const timingPointSplits = useMemo(() => data?.timingPointSplits ?? [], [data?.timingPointSplits]);
  const filterOptions = useMemo(() => ({
    genders: toFilterOptions(data?.filterOptions.genders ?? []),
    categories: categories.map((category: GraphCategory) => ({
      label: category.categoryName,
      value: String(category.id),
    })),
    ageGroups: toFilterOptions(data?.filterOptions.ageGroups ?? []),
    groups: toFilterOptions((draftSelectedCategory?.groups ?? []).map((group) => group.groupName)),
  }), [categories, data?.filterOptions, draftSelectedCategory]);
  const filteredRows = useMemo(
    () => filterRowsInCurrentPage(rows, {
      ...appliedFilters,
      ageGroup: collectBirth ? appliedFilters.ageGroup : '',
      gender: collectGender ? appliedFilters.gender : '',
    }),
    [rows, appliedFilters, collectBirth, collectGender]
  );
  const visibleRows = filteredRows;
  const resultColumns = useMemo(
    () => createResultColumns(timingPointSplits, recordDisplayMode),
    [timingPointSplits, recordDisplayMode]
  );
  const totalPages = Math.max(Math.ceil(totalCount / RESULT_PAGE_SIZE), 1);
  const currentPage = Math.min(page, totalPages);
  const pageStartIndex = totalCount === 0 ? 0 : (currentPage - 1) * RESULT_PAGE_SIZE + 1;
  const pageEndIndex = Math.min((currentPage - 1) * RESULT_PAGE_SIZE + rows.length, totalCount);
  const errorMessage = error instanceof Error ? error.message : '결과 데이터를 불러오지 못했습니다.';
  const canOpenResultDetail = managerRole !== null && managerRole !== 'NORMAL';

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (selectedEventId == null) {
      setEventDate('');
      return;
    }

    let ignore = false;

    eventService.detail(selectedEventId).then((event) => {
      if (!ignore) {
        setEventDate(event.eventDate);
      }
    }).catch(() => {
      if (!ignore) {
        setEventDate('');
      }
    });

    return () => {
      ignore = true;
    };
  }, [selectedEventId]);

  useEffect(() => {
    if (!isFetching && page > totalPages) {
      setPage(totalPages);
    }
  }, [isFetching, page, totalPages]);

  const updateDraftFilter = (key: keyof ResultFilters, value: string) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilterImmediately = (key: keyof ResultFilters, value: string) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
    setAppliedFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const applyCategoryFilterImmediately = (categoryId: string) => {
    const nextFilters = { categoryId, group: '' };

    setDraftFilters((current) => ({ ...current, ...nextFilters }));
    setAppliedFilters((current) => ({ ...current, ...nextFilters }));
    setPage(1);
  };

  const openEditModal = (row: ResultTableRow) => {
    const currentFinishStatus = getFinishStatus(row);

    setSelectedRow(row);
    setRecordDrafts(Object.fromEntries(
      row.splits.map((split) => [String(split.splitId), split.timingPointTime ?? ''])
    ));
    setModalError('');
    setModifyReasonDraft(getModifyReason(row) ?? '');
    setFinishStatusDraft(
      currentFinishStatus && currentFinishStatus !== 'NEEDS_REVIEW' ? currentFinishStatus : null
    );
    setSaveSuccess(false);
  };

  const closeEditModal = () => {
    setSelectedRow(null);
    setRecordDrafts({});
    setModifyReasonDraft('');
    setFinishStatusDraft(null);
    setModalError('');
    setSaveSuccess(false);
    setIsSaving(false);
  };

  const updateRecordDraft = (splitId: number, value: string) => {
    setRecordDrafts((current) => ({ ...current, [splitId]: value }));
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();

    const isSameQuery =
      page === 1 &&
      draftFilters.gender === appliedFilters.gender &&
      draftFilters.categoryId === appliedFilters.categoryId &&
      draftFilters.ageGroup === appliedFilters.ageGroup &&
      draftFilters.group === appliedFilters.group &&
      draftFilters.bibKeyword === appliedFilters.bibKeyword;

    setAppliedFilters(draftFilters);
    setPage(1);

    if (isSameQuery) {
      void refetch();
    }
  };

  const handleResetFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

  const handlePreviousPage = () => {
    setPage((current) => Math.max(current - 1, 1));
  };

  const handleNextPage = () => {
    setPage((current) => Math.min(current + 1, totalPages));
  };

  const openDownloadModal = () => {
    setDownloadFileName(createDefaultDownloadFileBaseName(selectedEventName, eventDate));
    setDownloadCategoryId(appliedFilters.categoryId);
    setDownloadError('');
    setDownloadModalOpen(true);
  };

  const closeDownloadModal = () => {
    if (downloadingFormat) {
      return;
    }

    setDownloadModalOpen(false);
    setDownloadError('');
  };

  const handleDownload = async (format: ResultDownloadFormat) => {
    if (selectedEventId == null) {
      setDownloadError('대회를 먼저 선택해 주세요.');
      return;
    }
    if (!downloadCategoryId) {
      setDownloadError('종목을 선택해 주세요.');
      return;
    }

    const fallbackFileName = createDefaultDownloadFileBaseName(selectedEventName, eventDate);
    const requestedFileName = sanitizeDownloadBaseName(downloadFileName.trim() || fallbackFileName);

    setDownloadingFormat(format);
    setDownloadError('');

    try {
      const response = await downloadResults(selectedEventId, {
        format,
        categoryId: Number(downloadCategoryId),
      });
      const blob = await fetchResultDownloadFile(response.downloadUrl);
      const downloadBlob = await toDownloadBlob(blob, format);

      triggerDownload(downloadBlob, withFileExtension(requestedFileName, format));
      setDownloadModalOpen(false);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : '결과 다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloadingFormat(null);
    }
  };

  const handleSaveResult = async () => {
    if (!selectedRow || selectedEventId == null) {
      return;
    }

    const splitsToSubmit: Array<{ splitId: number; timingPointTime: string }> = [];
    let hasInvalidSplit = false;

    selectedRow.splits.forEach((split) => {
      const value = recordDrafts[String(split.splitId)]?.trim() ?? '';

      if (!value) {
        return;
      }
      if (!TIME_INPUT_PATTERN.test(value)) {
        hasInvalidSplit = true;
        return;
      }
      splitsToSubmit.push({ splitId: split.splitId, timingPointTime: value });
    });

    if (hasInvalidSplit) {
      setModalError('기록은 HH:mm:ss 또는 HH:mm:ss.SSS 형식으로 입력해주세요.');
      return;
    }

    if (splitsToSubmit.length === 0 && !finishStatusDraft) {
      setModalError('수정할 구간 기록 또는 완주 상태 중 하나는 있어야 합니다.');
      return;
    }

    const modifyReason = modifyReasonDraft.trim();

    if (!modifyReason) {
      setModalError('수정 사유를 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setModalError('');

    try {
      await updateResult(selectedEventId, selectedRow.participantId, {
        modifyReason,
        splits: splitsToSubmit,
        finishStatus: finishStatusDraft ?? undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['results', selectedEventId] });
      setSaveSuccess(true);
    } catch (error) {
      setModalError(error instanceof Error ? error.message : '결과 수정 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  let tableContent: ReactNode = (
    <section className="results-table-panel" aria-busy={isFetching}>
      <DataTable
        columns={resultColumns}
        rows={visibleRows}
        rowKey="participantId"
        emptyMessage={showNeedsReviewOnly ? '검토필요한 기록이 없습니다.' : '표시할 결과 데이터가 없습니다.'}
        onRowClick={canOpenResultDetail ? openEditModal : undefined}
        rowSx={getResultRowSx}
      />
      <div className="results-pagination">
        <span>
          {totalCount === 0 ? '0건' : `${pageStartIndex}-${pageEndIndex} / ${totalCount}건`}
        </span>
        <div className="results-pagination-actions">
          <button type="button" onClick={handlePreviousPage} disabled={currentPage <= 1}>
            이전
          </button>
          <span>
            {currentPage} / {totalPages}
          </span>
          <button type="button" onClick={handleNextPage} disabled={currentPage >= totalPages}>
            다음
          </button>
        </div>
      </div>
    </section>
  );

  if (selectedEventId == null) {
    tableContent = (
      <div className="results-message">
        선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
      </div>
    );
  } else if (!draftFilters.categoryId) {
    tableContent = (
      <div className="results-message">종목을 선택한 뒤 결과를 조회할 수 있습니다.</div>
    );
  } else if (isLoading) {
    tableContent = <LoadingState message="결과 데이터를 불러오는 중입니다." />;
  } else if (rows.length === 0) {
    tableContent = <div className="results-message">표시할 결과 데이터가 없습니다.</div>;
  }

  return (
    <div className="results-page">
      <div className="results-title-row">
        <div className="page-title-block">
          <p className="page-section">Results</p>
          <h1>결과 조회</h1>
          <p className="page-description">칩타임 또는 측정시각 기준의 참가자별 기록을 조회하고, 필요한 경우 수정하거나 다운로드할 수 있습니다.</p>
        </div>
        <div className="results-title-actions">
          <div className="results-record-mode-toggle" aria-label="기록 표시 기준">
            {recordDisplayModeOptions.map((option) => (
              <button
                key={option.mode}
                className={recordDisplayMode === option.mode ? 'is-active' : undefined}
                type="button"
                aria-pressed={recordDisplayMode === option.mode}
                onClick={() => setRecordDisplayMode(option.mode)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={showNeedsReviewOnly ? 'results-missing-filter-toggle is-active' : 'results-missing-filter-toggle'}
            aria-pressed={showNeedsReviewOnly}
            onClick={() => {
              setShowNeedsReviewOnly((current) => !current);
              setPage(1);
            }}
          >
            검토필요만 보기
          </button>
          <button
            className="results-download-button"
            type="button"
            disabled={selectedEventId == null || isFetching}
            onClick={openDownloadModal}
          >
            결과 다운로드
          </button>
        </div>
      </div>

      <form className="results-filter-row" onSubmit={handleSubmit}>
        <FilterSelect
          label="종목"
          allLabel="종목 선택"
          value={draftFilters.categoryId}
          options={filterOptions.categories}
          disabled={filterOptions.categories.length === 0}
          onChange={applyCategoryFilterImmediately}
        />
        <FilterSelect
          label="그룹"
          allLabel="그룹 전체"
          value={draftFilters.group}
          options={filterOptions.groups}
          disabled={!draftFilters.categoryId || filterOptions.groups.length === 0}
          onChange={(value) => applyFilterImmediately('group', value)}
        />
        <FilterSelect
          label="나이그룹"
          allLabel="나이그룹 전체"
          value={collectBirth ? draftFilters.ageGroup : ''}
          options={filterOptions.ageGroups}
          disabled={!collectBirth || filterOptions.ageGroups.length === 0}
          onChange={(value) => applyFilterImmediately('ageGroup', value)}
        />
        <FilterSelect
          label="성별"
          allLabel="성별 전체"
          value={collectGender ? draftFilters.gender : ''}
          options={filterOptions.genders}
          disabled={!collectGender || filterOptions.genders.length === 0}
          onChange={(value) => applyFilterImmediately('gender', value)}
        />
        <input
          className="results-filter-control results-bib-input"
          type="text"
          placeholder="배번호, 칩번호, 이름 검색"
          value={draftFilters.bibKeyword}
          onChange={(event) => updateDraftFilter('bibKeyword', event.target.value)}
        />
        <button
          className="results-search-button"
          type="submit"
          disabled={isFetching || !draftFilters.categoryId}
          title={!draftFilters.categoryId ? '\uC885\uBAA9\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.' : undefined}
        >
          {"\uC870\uD68C"}
        </button>
        <button
          className="results-reset-button"
          type="button"
          disabled={isFetching}
          onClick={handleResetFilters}
        >
          {"\uCD08\uAE30\uD654"}
        </button>
      </form>

      {isError && (
        <div className="results-message results-error" role="alert">
          {errorMessage}
        </div>
      )}

      {tableContent}

      <Dialog
        open={Boolean(selectedRow)}
        onClose={blockBackdropClose(() => {
          if (!isSaving) {
            closeEditModal();
          }
        })}
        maxWidth="sm"
        fullWidth
      >
        {selectedRow && (saveSuccess ? (
          <>
            <DialogTitle sx={{ fontWeight: 800, fontSize: 18 }}>수정 완료</DialogTitle>
            <DialogContent>
              <Box sx={{ fontSize: 14, color: '#334155' }}>결과가 수정되었습니다.</Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                variant="contained"
                disableElevation
                onClick={closeEditModal}
                sx={{ backgroundColor: '#1da1f2', fontWeight: 700, '&:hover': { backgroundColor: '#1791db' } }}
              >
                확인
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <FormDialogTitle
              onClose={() => !isSaving && closeEditModal()}
              sx={{ fontWeight: 800, fontSize: 18 }}
            >
              참가자 정보
            </FormDialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 3 }}>
                {[
                  ['이름', selectedRow.name],
                  ['배번호', selectedRow.bibNumber],
                  ['칩번호', selectedRow.chipNumber],
                  ['종목', selectedRow.category],
                  ['그룹', selectedRow.group],
                  ['연령대', selectedRow.ageGroup],
                  ['성별', selectedRow.gender],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Box sx={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{label}</Box>
                    <Box sx={{ fontSize: 14, color: '#111827', fontWeight: 700 }}>{value}</Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 3,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: getFinishStatus(selectedRow) === 'NEEDS_REVIEW' ? '#fecaca' : '#e2e8f0',
                  backgroundColor: getFinishStatus(selectedRow) === 'NEEDS_REVIEW' ? '#fef2f2' : '#f8fafc',
                }}
              >
                <Box>
                  <Box sx={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>완주 상태</Box>
                  <Box sx={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>
                    현재: {getFinishStatusLabel(getFinishStatus(selectedRow))}
                  </Box>
                </Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={finishStatusDraft}
                  onChange={(_event, next: ConfirmableFinishStatus | null) => setFinishStatusDraft(next)}
                  aria-label="완주 상태 확정"
                >
                  <ToggleButton
                    value="FINISHED"
                    sx={{
                      fontWeight: 700,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        '&:hover': { backgroundColor: '#bbf7d0' },
                      },
                    }}
                  >
                    완주로 확정
                  </ToggleButton>
                  <ToggleButton
                    value="DNF"
                    sx={{
                      fontWeight: 700,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: '#e2e8f0',
                        color: '#334155',
                        '&:hover': { backgroundColor: '#cbd5e1' },
                      },
                    }}
                  >
                    중도포기로 확정
                  </ToggleButton>
                  <ToggleButton
                    value="DNS"
                    sx={{
                      fontWeight: 700,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: '#e2e8f0',
                        color: '#334155',
                        '&:hover': { backgroundColor: '#cbd5e1' },
                      },
                    }}
                  >
                    출발안함으로 확정
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>기록</Box>
              <Box sx={{ fontSize: 12, color: '#64748b', fontWeight: 700, mb: 1.5 }}>
                비워두면 해당 구간 기록은 변경되지 않습니다.
              </Box>
              <Box sx={{ display: 'grid', gap: 1.5, mb: 3 }}>
                {selectedRow.splits.map((split, index) => (
                  <TextField
                    key={split.splitId}
                    label={getSplitRecordLabel(split, index, selectedRow.splits.length)}
                    size="small"
                    fullWidth
                    placeholder="HH:mm:ss 또는 HH:mm:ss.SSS"
                    value={recordDrafts[String(split.splitId)] ?? ''}
                    onChange={(event) => updateRecordDraft(split.splitId, event.target.value)}
                  />
                ))}
              </Box>

              <TextField
                label="수정 사유"
                multiline
                minRows={3}
                fullWidth
                value={modifyReasonDraft}
                placeholder="수정 사유를 입력해주세요."
                inputProps={{ maxLength: 500 }}
                onChange={(event) => setModifyReasonDraft(event.target.value)}
              />

              {modalError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {modalError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={closeEditModal} disabled={isSaving} sx={{ color: '#8a94a6', fontWeight: 700 }}>
                취소
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={handleSaveResult}
                disabled={isSaving}
                sx={{ backgroundColor: '#1da1f2', fontWeight: 700, '&:hover': { backgroundColor: '#1791db' } }}
              >
                {isSaving ? '수정 중…' : '수정'}
              </Button>
            </DialogActions>
          </>
        ))}
      </Dialog>

      <Dialog
        open={downloadModalOpen}
        onClose={blockBackdropClose(closeDownloadModal)}
        maxWidth="xs"
        fullWidth
      >
        <FormDialogTitle onClose={closeDownloadModal} sx={{ fontSize: 20, fontWeight: 800 }}>
          결과 다운로드
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            <TextField
              label="다운로드 파일명"
              fullWidth
              value={downloadFileName}
              onChange={(event) => setDownloadFileName(sanitizeDownloadBaseName(event.target.value))}
              placeholder={createDefaultDownloadFileBaseName(selectedEventName, eventDate)}
            />
            <TextField
              label="종목"
              select
              fullWidth
              value={downloadCategoryId}
              disabled={Boolean(downloadingFormat) || filterOptions.categories.length === 0}
              onChange={(event) => setDownloadCategoryId(event.target.value)}
            >
              {filterOptions.categories.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Box sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569', mb: 0.75 }}>다운로드 형식</Box>
              <Box
                aria-label="다운로드 형식"
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}
              >
                {downloadFormats.map((item) => (
                  <Button
                    key={item.format}
                    disabled={Boolean(downloadingFormat) || !downloadCategoryId}
                    onClick={() => {
                      void handleDownload(item.format);
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      py: 2,
                      borderRadius: 2,
                      border: '1px solid #d8dee8',
                      color: '#111827',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#1da1f2', backgroundColor: '#f0f9ff' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        width: 44,
                        height: 52,
                        borderRadius: 1,
                        color: '#ffffff',
                        fontSize: 12,
                        fontWeight: 800,
                        backgroundColor:
                          item.format === 'pdf' ? '#dc2626' : item.format === 'xlsx' ? '#16a34a' : '#1da1f2',
                      }}
                    >
                      {item.label}
                    </Box>
                    <Box sx={{ fontSize: 13, fontWeight: 700 }}>
                      {downloadingFormat === item.format ? '다운로드 중' : item.label}
                    </Box>
                  </Button>
                ))}
              </Box>
            </Box>

            {downloadError && <Alert severity="error">{downloadError}</Alert>}
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ResultsDashboard;
